import { type ThunkAction, type ThunkDispatch, withExtraArgument } from 'redux-thunk';
import { default as createObserver } from 'src/utils/Redux/Observer';
import { extractNavPath } from './pages/NavigationShell/navigationUtils';
import { isSome } from 'fp-ts/lib/Option';
import service from 'src/ServiceContainer';
import { setAutoFreeze } from 'immer';
import { createEpicMiddleware } from 'redux-observable';
import { configureStore, Action, Tuple } from '@reduxjs/toolkit';
import { rootEpic } from './epics';
import { rootReducer } from './reducers';
import type { appReducer, AppState } from 'src/reducers';
import { persistStore } from 'redux-persist';
import serviceContainer from 'src/ServiceContainer';
import type ServiceContainer from 'src/ServiceContainer';
import { setGlobalDevModeChecks } from 'reselect';

// this disables the object freezing applied by @reduxjs/toolkit/node_modules/immer, which can cause rare issues when frozen objects are used downstream
setAutoFreeze(false);

// 'always' means this log to the console everytime a reselect selector that is not properly memoizing is called
setGlobalDevModeChecks({
  identityFunctionCheck: 'once',
  inputStabilityCheck: 'always',
});

export function clearCachedData() {
  service.pivotService.clearPivotCache();
}

// this is here in order to initialize state with the activeTab
// initializing state with this value avoids an extra call to setActiveTab which can trigger extraneous filter flushing
const maybeMatchHash = extractNavPath(window.location, 'hash');
const initState = {
  perspective: {
    activeTab: isSome(maybeMatchHash) ? maybeMatchHash.value[1] : '',
  },
} as ReturnType<typeof appReducer>;

// this injects the serviceEnv into all thunks and epics
const epicMiddleware = createEpicMiddleware<Action, Action<string>, AppState, typeof ServiceContainer>({
  dependencies: serviceContainer,
});
const thunkMiddlewareWithDependencies = withExtraArgument(serviceContainer);

// leaving here incase we want to turn back on
// const isDev = process.env.NODE_ENV !== 'production';

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      thunk: false,
      serializableCheck: false,
      immutableCheck: false,
    });
    return new Tuple(...defaultMiddleware, epicMiddleware, thunkMiddlewareWithDependencies);
  },
  devTools: true,
  preloadedState: initState,
});

export const persistor = persistStore(store);
epicMiddleware.run(rootEpic);

// TODO: sparingly used, prefer the thunk dispatch type if possible and remove this one
export type AppDispatch = typeof store.dispatch;

export const AppObserver = createObserver(store);
export default store;
